import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as NextArrow } from '../../assets/icons/next-arrow.svg';
import { ReactComponent as PreviousArrow } from '../../assets/icons/prev-arrow.svg';
import SelectFiled from '../select-field/SelectField';

interface PaginationProperties {
  activePage: number;
  handlePageChange: (pageNumber: number) => void;
  handleLimitChange: (changeLimit: number) => void;
  limit?: number;
  size?: number;
  totalResults?: number;
}

/**
 * Pagination component
 */
function Pagination({
  activePage,
  limit = 0,
  size = 0,
  totalResults,
  handlePageChange,
  handleLimitChange,
}: PaginationProperties) {
  const { t } = useTranslation('sharedComp');
  const step: number = 1;
  const showingNumbers = 3; // step * 2 + 1
  const startNumber: number = 2;
  const [startArrayNumber, setStartArrayNumber] = useState<number>(step);
  const [needStartDots, setNeedStartDots] = useState<boolean>(false);
  const [needEndDots, setNeedEndDots] = useState<boolean>(false);

  /**
   * Change page by clicking arrow
   * @param pageNumber
   */
  const handleArrowClick = (pageNumber: number, type: 'prev' | 'next') => {
    if (type === 'prev' && activePage > 1) {
      handlePageChange(pageNumber);
    } else if (type === 'next' && activePage < size) {
      handlePageChange(pageNumber);
    }
  };

  type DropdownOptionInfo = {
    label: string;
    value: string | number;
  };

  const dropDownOption: DropdownOptionInfo[] = [
    {
      label:  t("20"),
      value: 20,
    },
    {
      label: t("50"),
      value: 50,
    },
    {
      label: t("100"),
      value: 100,
    },
  ];

  useEffect(() => {
    if (activePage > step) {
      setStartArrayNumber(activePage - step);
      setNeedStartDots(activePage > step + startNumber);
    } else {
      setStartArrayNumber(step);
      setNeedStartDots(false);
    }

    if (size > showingNumbers) {
      setNeedEndDots(size > activePage + step + 1);

      if (size < activePage + step + 1) {
        setStartArrayNumber(size - showingNumbers);
      }
    }
  }, [activePage, step, size, startNumber, showingNumbers]);

  return (
    <div className="pagination-wrap">
      <div className="limit-container">
        <span>{t("itemsPerPage")}</span>
        <div className="limit-select-box">
          <SelectFiled
            inputLabel=""
            name="dropDown"
            selectedValue={limit}
            onChangeValue={(name, value) => handleLimitChange(value)}
            options={dropDownOption}
            // valueRequired
            // readOnly
          />
        </div>
        <span>{t("of")} {totalResults}</span>
      </div>
      <ul className="pagination-container">
        <li
          className={`pagination-arrow ${activePage > 1 ? 'active-arrow' : 'disable-arrow'}`}
          role="presentation"
          onClick={() => handleArrowClick(activePage - 1, 'prev')}>
          <PreviousArrow />
        </li>

        {size > showingNumbers + startNumber ? (
          <>
            <li
              role="presentation"
              onClick={(event) => handlePageChange(Number(event.currentTarget.textContent))}
              className={`pagination-item ${activePage === 1 ? 'active-item' : ''}`}>
              1
            </li>

            {needStartDots && (
              <div className="pagination-dots">
                <span>...</span>
              </div>
            )}

            {Array.from({ length: showingNumbers }, (_, index) => {
              const contentNumber = needStartDots ? startArrayNumber + index : startNumber + index;
              return (
                <li
                  key={index}
                  className={`pagination-item ${activePage === contentNumber ? 'active-item' : ''}`}
                  role="presentation"
                  onClick={() => handlePageChange(contentNumber)}>
                  {contentNumber}
                </li>
              );
            })}

            {needEndDots && (
              <div className="pagination-dots">
                <span>...</span>
              </div>
            )}

            <li
              className={`pagination-item ${activePage === size ? 'active-item' : ''}`}
              role="presentation"
              onClick={() => handlePageChange(size)}>
              {size}
            </li>
          </>
        ) : (
          Array.from({ length: size }, (_, index) => (
            <li
              key={index}
              className={`pagination-item ${activePage === index + 1 ? 'active-item' : ''}`}
              role="presentation"
              onClick={() => handlePageChange(index + 1)}>
              {index + 1}
            </li>
          ))
        )}

        <li
          className={`pagination-arrow ${activePage < size ? 'active-arrow' : 'disable-arrow'}`}
          role="presentation"
          onClick={() => handleArrowClick(activePage + 1, 'next')}>
          <NextArrow />
        </li>
      </ul>
    </div>
  );
}
Pagination.defaultProps = {
  size: 0,
  totalResults: 0,
  limit: 0,
};

export default Pagination;
